'use strict'

import { Manifest, SourceType, type MediaConfig } from '@foundation-player/loader'
import { type PlayerLoaderConfig, type UIConfig } from '@foundation-player/ui'
import { getBreakpoint } from '@/utils/app/detect'
import { type VideoModeValues } from '@/types/cms'
import { type VideoPlayerProps } from '@/types/video'
import { getAppConfig } from '@/config/config'

const config = getAppConfig()
const basePath = config.basePath

declare global {
  interface Window {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    $AC: any
  }
}

type VideoModeAttr = {
  readonly mediaId?: string
  readonly mediaType: SourceType
  readonly videoService: string
  readonly contentType: 'video' | 'videolivestream' | 'videoevent'
}

type VideoModeMap = Record<VideoModeValues, VideoModeAttr>

const videoModeMap: VideoModeMap = {
  vod: {
    mediaType: 'vod',
    videoService: 'ntv',
    contentType: 'video',
  },
  livestream: {
    mediaType: 'live',
    videoService: 'lvstr_ntv',
    contentType: 'videolivestream',
  },
  eventstream1world: {
    mediaId: 'ntvevent01',
    mediaType: 'event',
    videoService: 'lvstr_ntve',
    contentType: 'videoevent',
  },
  eventstream1de: {
    mediaId: 'ntvevent01de',
    mediaType: 'event',
    videoService: 'lvstr_ntve',
    contentType: 'videoevent',
  },
  eventstream2world: {
    mediaId: 'ntvevent02',
    mediaType: 'event',
    videoService: 'lvstr_ntve',
    contentType: 'videoevent',
  },
  eventstream2de: {
    mediaId: 'ntvevent02de',
    mediaType: 'event',
    videoService: 'lvstr_ntve',
    contentType: 'videoevent',
  },
}

export const createAdCall = (category: string, id: string, conlen: number) => {
  const baseUrl = 'https://ad-ipd.sxp.smartclip.net/select'

  const params = {
    type: 'vast4', // Definition der AdserverResponse
    fwd_adb: '[BLOCKED]', // placeholder
    s: category, //???
    sz: 'pre', // für Preroll. anpassen, wenn es weitere Slots gibt
    tagver: '{TAGVER}', // placeholder, Wert kommt aus dem Player
    fwd_comp: '0', // sind Companion Ads erlaubt? (1 ja, 0 nein?)
    ang_cntp: 'none', // Name des contentpartner
    ang_streamid: window.crypto && crypto.randomUUID ? crypto.randomUUID() : '', // random UUID
    ang_conlen: conlen.toString(), // ??? Content length, Länge des Content Videos in Sekunden
    fwd_conid: id, // Video ID
    rnd: (Math.floor(Math.random() * (9999999999999998 - 10000000)) + 10000000).toString(), // positive Zufallszahl, mindestens 8 Zeichen(i64)
    ang_plrw: '{PLAYER_WIDTH}', // Player Breite placeholder
    ang_plrh: '{PLAYER_HEIGHT}', // Player Hoehe placeholder
    consent: '{CONSENT}', // placeholder
  }

  let adCall = baseUrl + '?'
  for (const key in params) {
    adCall += key + '=' + params[key as keyof typeof params] + '&'
  }

  if (window.$AC && typeof window.$AC.setTargeting == 'function' && typeof window.$AC.getSXPKeys === 'function') {
    window.$AC.setTargeting('vcd', conlen)
    adCall += window.$AC.getSXPKeys()
  }

  return adCall
}

export const createUiConfig = (poster?: string): UIConfig => ({
  poster: poster,
  allowNativeVideoFullscreen: true,
})

export const createLoaderConfig = ({
  videoMode,
  isPurMode,
  isPreview,
  consentString,
}: {
  videoMode: keyof typeof videoModeMap
  isPurMode?: boolean
  isPreview?: boolean
  consentString?: string
}): PlayerLoaderConfig => ({
  client: 'rtldigitalnews',
  scope: 'ntv',
  environment: location.host.includes('preview.') || location.host.includes('beta.') ? 'preprod' : 'prod',
  pluginConfigs: {
    'foundation.plugin.heartbeat': {
      enabled: !isPurMode,
      endpoint: {
        vod: 'https://px1.vtrtl.de/vt/hb.do',
        live: 'https://px1.vtrtl.de/vtl/hb.do',
      },
      isKidsProfile: false,
      offer: getBreakpoint() === 'xs' ? 'mobntv' : 'ntv',
      payStatus: 0,
      userStatus: 0,
      videoService: videoModeMap[videoMode].videoService,
    },
    'foundation.plugin.googleAnalytics4': {
      enabled: !isPurMode,
      progressTrackingIntervalInSeconds: 15,
    },
    'foundation.plugin.nielsen': {
      ...(isPurMode ? { enabled: false } : {}),
    },
    'foundation.plugin.npawAnalytics': {
      ...(isPurMode ? { enabled: false } : {}),
    },
    'foundation.plugin.advertising': {
      ...(isPurMode || isPreview ? { enabled: false } : {}),
      smartclipConfig: {
        adReinsertion: {
          homad: {
            enabled: true,
            setup: {
              clientConfig: `${basePath}/configs/videoPlayer/homadClientConfig.json`,
            },
          },
        },
      },
    },
  },
  ...(consentString ? { consentString: consentString } : {}),
})

export const createMediaConfig = (props: VideoPlayerProps): MediaConfig => {
  const commentPath = (getBreakpoint() === 'xs' ? 'ntv-mew' : 'ntv') + '/' + props.trackingPath
  const videoMode = props.mode as keyof typeof videoModeMap

  return {
    id: videoModeMap[videoMode].mediaId ?? props.id,
    type: videoModeMap[videoMode].mediaType,
    autoplay: props.autoplay ?? false,
    muted: false,
    ...(!props.disableAds
      ? {
          advertising: {
            slots: [
              {
                adCall: createAdCall(props.category, props.id, props.lengthSeconds),
                offset: 0,
              },
            ],
          },
        }
      : {}),
    plugins: {
      'foundation.plugin.heartbeat': {
        //id: props.id,
        ivw: commentPath,
        startType: props.heartbeatStarttype ?? 0, // 1 für autom. videostart (playlist)
      },
      'foundation.plugin.googleAnalytics4': {
        //id: props.id,
        contentType: videoModeMap[videoMode].contentType,
        firstPublicationDate: props.publishedAt,
        headline: props.title,
        keywords: props.keywords,
      },
      'foundation.plugin.nielsen': {
        commentPath: commentPath,
      },
      'foundation.plugin.npawAnalytics': {
        channelName: 'ntv',
        //id: props.id,
        title: props.title,
      },
    },
    metadata: {
      poster: props.poster ? new URL(props.poster).pathname : undefined,
      firstPublicationDate: props.publishedAt,
    },
    manifests: [
      ...(props.source.progressive
        ? [
            {
              type: 'progressive',
              sources: [
                {
                  url: props.source.progressive,
                  priority: 'main',
                },
              ],
            } as Manifest,
          ]
        : []),
      ...(props.source.dash
        ? [
            {
              type: 'dashhd',
              sources: [
                {
                  url: props.source.dash,
                  priority: 'main',
                },
              ],
              licenseServers: [
                {
                  address: 'https://widevine.n-tv.de/index/proxy',
                  encryptionType: 'widevine',
                },
                {
                  address: 'https://playready-core.n-tv.de/playready/api/RightsManager.asmx',
                  encryptionType: 'playready',
                },
              ],
            } as Manifest,
          ]
        : []),
      ...(props.source.hls
        ? [
            {
              type: 'hlsfairplayhd',
              sources: [
                {
                  url: props.source.hls,
                  priority: 'main',
                },
              ],
              licenseServers: [
                {
                  address: 'https://fairplay.n-tv.de/Fairplay/GetLicense',
                  certificate: 'https://fairplay.n-tv.de/Certificate/RTLiDe_cert.cer',
                  encryptionType: 'fairplay',
                },
              ],
            } as Manifest,
          ]
        : []),
    ],
  }
}
